import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const AccountSecurity = ({ user, onSuccess }) => {
  const { resetPassword } = useAuth();
  const [resetEmail, setResetEmail] = useState(user?.email || '');
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState(null);

  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    setIsResetting(true);
    setError(null);

    try {
      if (!resetEmail?.trim()) {
        throw new Error('Email is required');
      }

      const { error: resetErr } = await resetPassword(resetEmail?.trim());

      if (resetErr) {
        throw new Error(resetErr?.message || 'Failed to send reset email');
      }

      onSuccess?.('Password reset email sent! Check your inbox.');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err?.message || 'Failed to send reset email');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Account Security
        </h2>
        <p className="text-blue-100">
          Manage your password and security settings
        </p>
      </div>

      {error && (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-red-800" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Password Reset */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Key" size={24} className="text-yellow-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Password</h3>
            <p className="text-blue-200 text-sm">Reset your account password</p>
          </div>
        </div>

        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div>
            <label className="block text-white font-medium mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e?.target?.value)}
              placeholder="Enter your email"
              className="bg-white bg-opacity-20 text-white placeholder-blue-200 border-white border-opacity-20"
            />
          </div>

          <Button
            type="submit"
            variant="secondary"
            disabled={isResetting}
            iconName="Mail"
            iconPosition="left"
            className="bg-white bg-opacity-20 text-white hover:bg-opacity-30"
          >
            {isResetting ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </form>
      </div>

      {/* Login Activity (Placeholder) */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Activity" size={24} className="text-green-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Login Activity</h3>
            <p className="text-blue-200 text-sm">Monitor your recent login sessions</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
            <div className="flex items-center space-x-3">
              <Icon name="Monitor" size={20} className="text-blue-300" />
              <div>
                <p className="text-white font-medium">Current Session</p>
                <p className="text-blue-200 text-sm">Active now</p>
              </div>
            </div>
            <span className="text-green-400 text-sm font-medium">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurity;